import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classnames from 'clsx';
import DisplayAd from 'src/common/components/ads/DisplayAd';
import { isSmall } from 'src/common/utils/breakpoints';
import { AdSettingsProvider } from '../../contexts/AdSettingsContext';
import keyMirror from '../../utils/keyMirror';
import determineAdEligibility from '../../utils/ads/determineAdEligibility';
import LoadMoreItems from './shared/LoadMoreItems';
import ListLayout from './list/ListLayout';
import GalleryLayout from './gallery/Gallery';
import LinkPresentation from './childPresentations/Link';
import NumberedLinkPresentation from './childPresentations/NumberedLink';
import ScheduleCard from './scheduleCard/ScheduleCard';
import Prompt from './shared/Prompt';
import Matrix from './matrix/Matrix';
import Flow from './flow/Flow';
import getStylesCreator from './utils/getStylesCreator';
import css from './container-items.module.scss';

const layoutMap = {
  Gallery: GalleryLayout,
  Carousel: GalleryLayout,
  NumberedMatrix: NumberedLinkPresentation,
  LinkMatrix: LinkPresentation,
  Flow,
  Prompt,
  Matrix,
  TileMatrix: Matrix,
  ScheduleCard,
};

const reducedMarginItemsMap = keyMirror(
  'ItemCard',
  'EpisodeCard',
  'ScheduleCard',
);

function getLayout(layout) {
  return layoutMap[layout] || ListLayout;
}

const ContainerItems = ({
  items,
  itemsStyles,
  isFetching,
  breakpoint,
  routeProps,
  selectedContentId,
  className,
  isDarkMode,
  isProfilePage,
  onItemClick,
  disableFirstContainerAd = false,
  guideItem,
}) => {
  const canShowAds = useSelector(determineAdEligibility(guideItem));

  if (!items || !items.length) {
    return null;
  }

  const matchUrl = routeProps?.matchUrl;

  return (
    // data-darkmode attribute is used to apply changes to child components via css
    <div
      className={className}
      style={{ opacity: isFetching ? 0.5 : 1 }}
      data-darkmode={isDarkMode}
    >
      <AdSettingsProvider
        isAdEligible={canShowAds}
        isProfilePage={isProfilePage}
      >
        {items.map((item, i) => {
          const { containerNavigation } = item;
          const layout = item?.presentation?.layout;
          const Layout = getLayout(layout);
          const isFirstItem = i === 0;
          const getStyles = getStylesCreator(itemsStyles, item.style);
          const { divider: showDivider } = getStyles();
          const isReducedMargin =
            reducedMarginItemsMap[item?.containerType] &&
            reducedMarginItemsMap[items[i + 1]?.containerType];

          return (
            <div
              className={classnames(css.containerItem, {
                [css.reducedMargin]: isReducedMargin,
              })}
              key={`${selectedContentId}-${i}`}
            >
              <Layout
                matchUrl={matchUrl}
                containerItem={item}
                getStyles={getStyles}
                breakpoint={breakpoint}
                index={i}
                isDarkMode={isDarkMode}
                selectedContentId={selectedContentId}
                id={`container-${i}`}
                onItemClick={onItemClick}
              />
              {containerNavigation && containerNavigation.destinationInfo && (
                <LoadMoreItems
                  containerNavigation={containerNavigation}
                  index={i}
                  offset={item.children.length}
                  selectedContentId={selectedContentId}
                />
              )}
              {isFirstItem &&
                !isSmall(breakpoint) &&
                canShowAds &&
                !disableFirstContainerAd && (
                  <DisplayAd
                    id="browse_mid_min"
                    slotName="browse_mid"
                    style={{ padding: '15px 0' }}
                    guideId={routeProps?.guideContext?.guideId}
                    matchUrl={matchUrl}
                  />
                )}
              {showDivider && (
                <div className={css.containerDivider}>
                  <hr />
                </div>
              )}
            </div>
          );
        })}
      </AdSettingsProvider>
    </div>
  );
};

ContainerItems.propTypes = {
  guideItem: PropTypes.object,
  items: PropTypes.array.isRequired,
  itemsStyles: PropTypes.object,
  isFetching: PropTypes.bool.isRequired,
  breakpoint: PropTypes.number.isRequired,
  selectedContentId: PropTypes.string.isRequired,
  routeProps: PropTypes.object,
  className: PropTypes.string,
  isDarkMode: PropTypes.bool.isRequired,
  isProfilePage: PropTypes.bool,
  onItemClick: PropTypes.func,
  disableFirstContainerAd: PropTypes.bool,
};

ContainerItems.defaultProps = {
  itemsStyles: {},
  className: '',
  isDarkMode: false,
  isProfilePage: false,
  onItemClick: () => {},
};

export default ContainerItems;
